// boot-encryption.ts — the boot-time E2E-encryption provisioning state machine,
// extracted verbatim from app-init.ts (review #13). The highest-blast-radius path
// in the app: it decides, on every boot, whether to load the local master key,
// auto-provision a fresh one (truly new account), surface needsKey (existing
// account / fresh device), or honour the post-reset marker (reset-account.ts),
// then wraps window.NoteStorage with the workspace's data key. It closes over no
// boot-scope state — every dependency is a module import below — so it moved as a
// standalone function, callable from app-init's boot sequence and the
// _syncHelpers.reinitEncryption in-place hook.

import { CryptoEngine } from '../encryption/crypto-engine.js';
import { CryptoStorage } from '../encryption/crypto-storage.js';
import { DevicePairing } from '../encryption/device-pairing.js';
import { KeyStorage } from '../encryption/key-storage.js';
import { setEncryption } from '../encryption/state.js';
import { WorkspaceKeys } from '../encryption/workspace-keys.js';
import { getSupabaseClient, getSyncHelpers } from '../storage/session.js';

export async function initEncryptionAndWrap(): Promise<void> {
  if (getSyncHelpers()?.authenticated && getSupabaseClient()) {
    try {
      const session = await getSupabaseClient().auth.getSession();
      const userId = session?.data?.session?.user?.id;
      if (userId) {
        setEncryption({ userId });

        let masterKey = null;
        const rawKey = await KeyStorage.loadMasterKey(userId);
        if (rawKey) {
          masterKey = await CryptoEngine.importKey(rawKey as any);
          // Recover from a previous boot that saved the key locally but
          // failed to publish the keypair (e.g. publishStaticKeypair
          // returned false on a transient Supabase error). Idempotent —
          // a no-op when the row already matches this master key.
          // Failures here must not abort encryption setup: the local
          // key is fine, only the public-key advertisement is missing.
          try {
            await WorkspaceKeys?.publishStaticKeypair?.(userId, masterKey);
          } catch (e: any) {
            console.warn('[encryption] Idempotent keypair re-publish failed:', e?.message);
          }
        } else {
          // Local key missing. Decide whether to auto-provision (truly
          // fresh account) or set needsKey (existing account, fresh
          // device). hasEncryptionRow throws on transient errors so we
          // never auto-provision on an unverifiable answer.
          let initialised: any;
          // After an in-app encryption RESET (reset-account.ts) the
          // user_encryption row was just deleted and this marker set. A
          // Supabase read replica may still report the row as present, which
          // would wrongly strand the user on needsKey. Honour the marker:
          // re-delete on THIS connection (defeating the lag), then force the
          // fresh-account branch.
          if (localStorage.getItem('ariadne_reset_provision') === userId) {
            localStorage.removeItem('ariadne_reset_provision');
            try {
              await getSupabaseClient().from('user_encryption').delete().eq('user_id', userId);
            } catch (e: any) {
              console.warn('[encryption] reset re-delete of user_encryption failed:', e?.message);
            }
            initialised = false;
            console.log('[encryption] Post-reset: forcing fresh-account provision.');
          } else {
            try {
              initialised = await DevicePairing.hasEncryptionRow(userId);
            } catch (e: any) {
              console.error(
                '[encryption] Could not verify encryption row; refusing to auto-provision:',
                e
              );
              // Conservative default: surface the pairing UI. If this is
              // really a fresh account, the user can reload once the
              // network is back; auto-provisioning here would risk
              // overwriting an existing row on a transient error.
              setEncryption({ needsKey: true });
              initialised = null;
            }
          }

          if (initialised === true) {
            // Existing account, fresh device — pair to receive the key.
            setEncryption({ needsKey: true });
            console.log('[encryption] Existing account, no local key. Device pairing required.');
          } else if (initialised === false) {
            // Fresh account — auto-provision so encryption is on from
            // the very first save. We persist the key locally BEFORE
            // publishing the row so a failed publish leaves the next
            // boot able to retry from scratch (the key is in
            // KeyStorage but no row is on the server, so the next boot
            // takes the rawKey-found branch and idempotently
            // re-publishes).
            const candidate = await CryptoEngine.generateMasterKey();
            const newKeyBytes = await CryptoEngine.exportKey(candidate);
            await KeyStorage.saveMasterKey(newKeyBytes, userId);

            // Read-after-write check. Without this, a silent persist
            // failure (browser quota, IDB rolled back, safeStorage
            // unavailable) would leave the row published but no local
            // key — i.e. exactly the "needs key from another device"
            // state the user reported. Bail BEFORE publishing.
            const verify = await KeyStorage.loadMasterKey(userId);
            const verifyOk =
              verify &&
              verify.length === newKeyBytes.length &&
              newKeyBytes.every((b, i) => b === verify[i]);
            if (!verifyOk) {
              throw new Error(
                'Failed to persist encryption key locally; refusing to ' +
                  'publish keypair. Check browser storage permissions and reload.'
              );
            }

            try {
              await WorkspaceKeys?.publishStaticKeypair?.(userId, candidate);
              await DevicePairing.registerDevice(userId);
              masterKey = candidate;
              console.log('[encryption] New account auto-provisioned with E2E encryption.');
            } catch (e: any) {
              if (e?.code === 'MASTER_KEY_MISMATCH') {
                // hasEncryptionRow said "no row" but publishStaticKeypair
                // found one with a different master key. That means our
                // hasEncryptionRow check raced or was wrong; treat as
                // existing-account-fresh-device and roll back the local
                // key we just saved (it's the wrong one — it would
                // decrypt nothing).
                console.warn(
                  '[encryption] Auto-provision detected existing keypair; rolling back.'
                );
                await KeyStorage.deleteMasterKey(userId).catch(() => {});
                setEncryption({ needsKey: true });
              } else {
                throw e;
              }
            }
          }
        }

        if (masterKey) {
          setEncryption({ key: masterKey, active: true });

          // Sweep any pending workspace-key entries left in localStorage
          // by an earlier accept-invite that landed before this device
          // had a master key. Best-effort — failures must not abort
          // encryption setup.
          try {
            await WorkspaceKeys?.claimPendingFromLocalStorage?.(userId, masterKey);
          } catch (e: any) {
            console.warn('[encryption] claimPendingFromLocalStorage failed:', e?.message);
          }

          // Finish any rotation that was interrupted on this device
          // before the UI binds to a workspace. A killed rotation
          // leaves local attachments under the old key while the
          // server (and other devices) have moved to the new one;
          // without this resume, those attachments would be
          // permanently unreadable. Best-effort — failures leave the
          // checkpoint in place for the next boot.
          try {
            await WorkspaceKeys?.resumeInterruptedRotations?.(masterKey);
          } catch (e: any) {
            console.warn('[encryption] resumeInterruptedRotations failed:', e?.message);
          }

          // Pick the right key for the active workspace. Personal
          // workspaces (workspace_id == userId) use the master key
          // directly. Shared workspaces have their own AES-GCM key,
          // wrapped per-member under the master key in workspace_keys.
          //
          // Capture wsId once and re-check it just before wrapping so a
          // cross-tab workspace switch landing during the (potentially
          // multi-second) WorkspaceKeys fetch can't bind the wrap to the
          // wrong workspace. If the value moved, abort init and let the
          // outer try/catch log; the next reload will pick up the new
          // workspace cleanly.
          const wsId = localStorage.getItem('current_workspace_id') || userId;
          const { dataKey, missing: workspaceKeyMissing } = WorkspaceKeys
            ? await WorkspaceKeys.selectDataKey(wsId, userId, masterKey)
            : { dataKey: masterKey, missing: false };
          // A shared workspace whose key we have binds the wrap to that
          // workspace id; a missing key wraps with the master key + the
          // readonly UI branch handles it (selectDataKey logged why).
          if (!workspaceKeyMissing && wsId !== userId) {
            setEncryption({ workspaceId: wsId });
          }
          const wsIdNow = localStorage.getItem('current_workspace_id') || userId;
          if (wsIdNow !== wsId) {
            throw new Error(
              `[encryption] active workspace changed mid-init (${wsId} -> ${wsIdNow}); ` +
                `aborting wrap so the next reload can bind to the right key.`
            );
          }
          setEncryption({ dataKey, workspaceKeyMissing });

          // Wrap NoteStorage with the chosen key (workspace key for
          // shared workspaces, master key otherwise).
          window.NoteStorage = await CryptoStorage.wrap(window.NoteStorage, dataKey);
          console.log('[encryption] E2E encryption active.');
        }
      }
    } catch (e: any) {
      // PRF mismatch is a distinct, unrecoverable failure — the user
      // satisfied the WebAuthn ceremony but the resulting bytes don't
      // decrypt the stored wrap. Surface a dedicated flag so the UI
      // can prompt for re-pairing instead of a transient retry.
      if (e?.name === 'PasskeyPrfMismatchError') {
        setEncryption({ prfMismatch: true, needsKey: true });
        console.error(
          '[encryption] Passkey PRF mismatch — wrapped key cannot be unlocked with this authenticator. Re-pair from another device.'
        );
      } else {
        console.error('[encryption] Key loading failed:', e);
      }
    }
  }

  // Encryption setup has settled — announce it ONCE for whatever terminal
  // state we reached: active key, needs-key/pairing, PRF mismatch, or an
  // inert unauthenticated sandbox. Consumers read getEncryption() to see
  // which. Firing UNCONDITIONALLY (not only for the active/sandbox states the
  // old guard allowed) is what lets the IN-PLACE sign-in repaint an already-
  // open settings panel into the needs-key pairing UI — otherwise unreachable,
  // since the panel only mounts that section on this event and the boot reload
  // that used to rebuild it is gone. Symmetric with the sign-OUT path
  // (workspace-switcher reinitLocalStorageWrap), which already fires
  // encryption:ready after clearing the keys for the same re-render reason.
  // The only other listener (pending convert/migration) is a no-op without its
  // sessionStorage marker, so an early fire on the needs-key path is safe.
  window.dispatchEvent(new CustomEvent('encryption:ready'));
}
